import {
  Fragment,
  createContext,
  createElement,
  useContext,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from "react";

/*
 * WP4.6a: true inside `GridPageLayoutV2` (the new-UI module screen). Holds no design-system code, so the classic
 * `GridPageLayout.tsx` may import it eagerly: its `GridPageHeaderLeft` (used inside module custom headers — Files,
 * Mailbox) switches to the restyled header only while this is true. Outside a V2 module screen it is false and
 * every classic path is unchanged.
 */
export const ModuleLayoutContext = createContext<boolean>(false);

export function useInModuleLayoutV2(): boolean {
  return useContext(ModuleLayoutContext);
}

/*
 * New UI: true inside the shell's page frame (`ClassicPageFrame page`, a classic page body in the new shell, plan-ds
 * §4.13). The classic `GridPageLayout` then drops its outer `#F0F5F9` page fill: classic draws it on a page of the
 * same colour, so it is invisible there, but on the shell's page colour it shows as a strip beside the card. False
 * everywhere else (settings sections, dialogs, the flag-off app), where nothing changes.
 */
export const ShellPageFrameContext = createContext<boolean>(false);

export function useInShellPageFrame(): boolean {
  return useContext(ShellPageFrameContext);
}

/** A component that wraps classic content in the shell's classic frame. */
export type ClassicFrame = ComponentType<{ children: ReactNode }>;

/*
 * New UI: the frame classic content takes when the shell renders it in a DS body (close holes, DP-4, 2026-10-14). A
 * route's crash fallback (Router's ErrorBoundary) replaces the route's content where the shell draws it; in a V2 body
 * (Portal Settings, module screens) nothing framed it, so it sat under the DS root: its `bg-white` page stayed white
 * while the dark DS ink coloured its text (unreadable), and classic dark could not reach it on a certified route.
 * The shell provides ClassicPageFrame here for V2 bodies (ShellLayoutView); a frame provides null to its subtree
 * (already framed); the classic chrome provides nothing, so the flag-off app renders exactly as before.
 */
export const ClassicFrameContext = createContext<ClassicFrame | null>(null);

/** `children` in the frame the shell provides (`ClassicFrameContext`), or as they are where it provides none. */
export function FramedClassic({ children }: { children: ReactNode }): ReactElement {
  const Frame = useContext(ClassicFrameContext);
  return Frame === null ? createElement(Fragment, null, children) : createElement(Frame, null, children);
}
